import React, { useEffect } from 'react';
import { FileText, Pin, Search as SearchIcon, Plus } from 'lucide-react';
import { useStore, selectVisibleNotes } from '../store';
import { authApi } from '../api/client';
import { useDarkMode } from '../hooks/useDarkMode';
import { useShallow } from 'zustand/shallow';
import { useKeyboard } from '../hooks';
import { sortNotes } from '../utils';
import { Spinner, EmptyState, Button, GlobalSpin } from '../components/ui';
import Sidebar from '../components/layout/Sidebar';
import TopBar from '../components/layout/TopBar';
import MobileSidebar from '../components/layout/MobileSidebar';
import NoteCard from '../components/dashboard/NoteCard';
import NoteEditor from '../components/editor/NoteEditor';
import VerifyBanner from '../components/ui/VerifyBanner';
import type { Note } from '../types';
import ConfirmModal from '../components/ui/ConfirmModal';

const Dashboard: React.FC = () => {
  // ── Every selector is individual — NO inline objects ──────────────────────
  const loadNotes     = useStore(s => s.loadNotes);
  const loadFolders   = useStore(s => s.loadFolders);
  const notesLoading  = useStore(s => s.notesLoading);
  const openNoteId    = useStore(s => s.openNoteId);
  const activeView    = useStore(s => s.activeView);
  const searchQuery   = useStore(s => s.searchQuery);
  const searchResults = useStore(s => s.searchResults);
  const createNote    = useStore(s => s.createNote);
  const setOpenNote   = useStore(s => s.setOpenNote);
  const sidebarOpen   = useStore(s => s.sidebarOpen);

  // useShallow prevents infinite loop for array-returning selectors
  const visibleNotes  = useStore(useShallow(selectVisibleNotes));
  const [deleteTarget, setDeleteTarget] = React.useState<{noteId:string;noteTitle:string}|null>(null);
  const pinned        = visibleNotes.filter(n => n.isPinned);
  const recent        = visibleNotes.filter(n => !n.isPinned);

  const { isDark } = useDarkMode();
  // Refresh user after email verification
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified') === '1') {
      authApi.me().then(freshUser => {
        useStore.setState({ user: freshUser });
        // Clean URL
        window.history.replaceState({}, '', '/dashboard');
      }).catch(() => {});
    }
    loadNotes();
    loadFolders();
  }, []); // eslint-disable-line
  useKeyboard();

  // Listen for delete confirmation requests from NoteCard
  useEffect(() => {
    const handler = (e: Event) => {
      const { noteId, noteTitle } = (e as CustomEvent).detail;
      setDeleteTarget({ noteId, noteTitle });
    };
    window.addEventListener('Jötr:confirm-delete', handler);
    return () => window.removeEventListener('Jötr:confirm-delete', handler);
  }, []);

  const handleNewNote = async () => {
    const note = await createNote();
    setOpenNote(note.id);
  };

  return (
    <>
      <GlobalSpin/>
      <VerifyBanner/>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body, #root { height: 100%; }
        body { font-family: 'DM Sans', sans-serif; background: #fff; color: #1A1814; overflow: hidden; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-thumb { background: #E8E4DC; border-radius: 3px; }
        /* Hide desktop sidebar on mobile — MobileSidebar handles it as a drawer */
        @media(max-width:768px){
          .app-sidebar      { display: none !important; }
          .notes-grid       { grid-template-columns: 1fr !important; }
          /* On mobile, editor opens full screen over the note list */
          .mobile-note-list { display: flex !important; }
          .editor-panel     {
            position: fixed !important;
            inset: 0 !important;
            z-index: 100 !important;
            background: #fff !important;
            flex: none !important;
            width: 100% !important;
            height: 100vh !important;
          }
        }
      `}</style>

      <div data-theme={isDark ? 'dark' : 'light'} style={{ display:'flex', height:'100vh', overflow:'hidden', background: isDark ? '#1A1814' : '#fff', color: isDark ? '#F5F0E8' : '#1A1814' }}>
        {sidebarOpen && <div className="app-sidebar"><Sidebar/></div>}
        <MobileSidebar/>

        <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0, overflow:'hidden' }}>
          <TopBar/>
          <div style={{ flex:1, display:'flex', overflow:'hidden' }}>
            <div style={{
              flex: openNoteId ? '0 0 340px' : 1,
              borderRight: openNoteId ? '1px solid #E8E4DC' : 'none',
              overflowY: 'auto',
              background: '#FAFAF8',
            }}>
              {notesLoading
                ? <Spinner/>
                : activeView === 'search'
                  ? <SearchResultsView results={searchResults} query={searchQuery}/>
                  : <NotesList pinned={pinned} recent={recent} searchQuery={searchQuery} onNewNote={handleNewNote}/>
              }
            </div>
            {openNoteId && <div className="editor-panel" style={{ flex:1, minWidth:0 }}><NoteEditor/></div>}
          </div>
        </div>
      </div>
      {/* Delete confirmation modal — rendered at dashboard level so it's never blocked */}
      <ConfirmModal
        open={!!deleteTarget}
        title="Delete this note?"
        message={`"${deleteTarget?.noteTitle || 'This note'}" will be permanently deleted and cannot be recovered.`}
        confirmLabel="Yes, delete forever"
        onConfirm={() => { if (deleteTarget) { useStore.getState().deleteNote(deleteTarget.noteId); setDeleteTarget(null); } }}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

// ─── Notes List ───────────────────────────────────────────────────────────────
interface NotesListProps {
  pinned: Note[];
  recent: Note[];
  searchQuery: string;
  onNewNote: () => void;
}

const NotesList: React.FC<NotesListProps> = ({ pinned, recent, searchQuery, onNewNote }) => {
  if (pinned.length === 0 && recent.length === 0) return (
    <EmptyState
      icon={<FileText size={22}/>}
      title="No notes yet"
      desc="Create your first note — everything you write is instantly searchable."
      action={<Button onClick={onNewNote}><Plus size={14}/> Create note</Button>}
    />
  );

  const sorted = (arr: Note[]) => sortNotes(arr) as Note[];

  return (
    <div style={{ padding:'20px 20px 40px' }}>
      {pinned.length > 0 && (
        <div style={{ marginBottom:28 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:12 }}>
            <Pin size={13} color="#7A7570"/>
            <span style={{ fontSize:11, fontWeight:700, color:'#7A7570', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'DM Sans',sans-serif" }}>
              Pinned
            </span>
          </div>
          <div className="notes-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:12 }}>
            {sorted(pinned).map(n => <NoteCard key={n.id} note={n} searchQuery={searchQuery}/>)}
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
            <span style={{ fontSize:11, fontWeight:700, color:'#7A7570', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:"'DM Sans',sans-serif" }}>
              {pinned.length > 0 ? 'Recent' : 'Notes'}
            </span>
            <span style={{ fontSize:11, color:'#7A7570', fontFamily:"'DM Sans',sans-serif" }}>
              {recent.length} note{recent.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="notes-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:12 }}>
            {sorted(recent).map(n => <NoteCard key={n.id} note={n} searchQuery={searchQuery}/>)}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Search Results ───────────────────────────────────────────────────────────
interface SearchResultsViewProps {
  results: { note: Note; snippet: string; matchCount: number }[];
  query: string;
}

const SearchResultsView: React.FC<SearchResultsViewProps> = ({ results, query }) => {
  if (!query) return (
    <EmptyState
      icon={<SearchIcon size={22}/>}
      title="Search your notes"
      desc="Type anything in the search bar above."
    />
  );
  if (results.length === 0) return (
    <EmptyState
      icon={<SearchIcon size={22}/>}
      title={`No results for "${query}"`}
      desc="Try a different word or check your spelling."
    />
  );
  return (
    <div style={{ padding:'20px 20px 40px' }}>
      <p style={{ fontSize:12, color:'#7A7570', marginBottom:16, fontFamily:"'DM Sans',sans-serif" }}>
        {results.length} result{results.length !== 1 ? 's' : ''} for{' '}
        "<strong style={{ color:'#C4811A' }}>{query}</strong>"
      </p>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
        {results.map(r => <NoteCard key={r.note.id} note={r.note} searchQuery={query}/>)}
      </div>
    </div>
  );
};

export default Dashboard;